import React, { useState, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FiUpload, FiDownload, FiFileText, FiImage } from "react-icons/fi";
import { FaRegFileWord } from "react-icons/fa";
import * as docx from "docx";
import { saveAs } from "file-saver";
import { createWorker } from "tesseract.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { FaSpinner, FaTrash } from 'react-icons/fa';

export default function JpgToWordConverter() {
  const [imageUrl, setImageUrl] = useState(null);
  const { darkMode } = useTheme();
  const [fileName, setFileName] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extractedText, setExtractedText] = useState("");
  const [ocrStatus, setOcrStatus] = useState("");
  const [mode, setMode] = useState("embed"); // 'embed' or 'ocr'
  const [ocrOptions, setOcrOptions] = useState({
    language: 'eng',
    preserveInterwordSpaces: true,
    tessedit_pageseg_mode: '6' // Assume a single uniform block of text
  });
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    const isImage = validTypes.includes(file.type) || 
                   file.name.toLowerCase().match(/\.(jpe?g|png)$/);

    if (!isImage) {
      alert("Please upload a valid image file (JPEG or PNG)");
      return;
    }

    setIsConverting(true);
    setFileName(file.name);
    setProgress(30);
    setExtractedText("");
    setOcrStatus("");

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageUrl(e.target.result);
      setProgress(100);
      setIsConverting(false);
      setTimeout(() => setProgress(0), 500);
    };
    reader.readAsDataURL(file);
  };

  const performOCR = async () => {
    if (!imageUrl) return;

    try {
      setIsConverting(true);
      setProgress(0);
      setOcrStatus("Initializing OCR engine...");

      const worker = await createWorker();
      
      // Set up progress monitoring using the proper method
      worker.setProgressHandler((progress) => {
        const progressValue = Math.min(90, Math.floor(progress.progress * 100));
        setProgress(progressValue);
        setOcrStatus(`Processing: ${progressValue}%`);
      });

      setOcrStatus("Loading language data...");
      await worker.loadLanguage(ocrOptions.language);
      await worker.initialize(ocrOptions.language);
      
      // Set OCR parameters for better accuracy
      await worker.setParameters({
        preserve_interword_spaces: ocrOptions.preserveInterwordSpaces ? '1' : '0',
        tessedit_pageseg_mode: ocrOptions.tessedit_pageseg_mode
      });

      setOcrStatus("Processing image...");
      setProgress(30);
      
      const { data } = await worker.recognize(imageUrl);
      
      setExtractedText(data.text);
      setOcrStatus("Text extraction complete!");
      setProgress(100);
      
      await worker.terminate();
    } catch (error) {
      console.error("OCR Error:", error);
      setOcrStatus("Error during text extraction");
      alert(`Failed to extract text: ${error.message}. Please try again with a clearer image.`);
    } finally {
      setIsConverting(false);
      setTimeout(() => setProgress(0), 500);
    }
  };

  const handleConvertToWord = async () => {
    if (!imageUrl) return;

    try {
      setIsConverting(true);
      setProgress(0);

      if (mode === "ocr") {
        if (!extractedText) {
          await performOCR();
          if (!extractedText) {
            throw new Error("No text was extracted from the image");
          }
        }
        
        // Create document with extracted text
        const doc = new docx.Document({
          sections: [{
            properties: {},
            children: [
              new docx.Paragraph({
                children: [
                  new docx.TextRun({
                    text: extractedText,
                    size: 24, // 12pt
                  }),
                ],
              }),
            ],
          }],
        });

        setProgress(80);
        const blob = await docx.Packer.toBlob(doc);
        saveAs(blob, `${fileName.replace(/\.[^/.]+$/, "")}_text.docx`);
      } else {
        // Original image embedding functionality
        const doc = new docx.Document({
          sections: [{
            properties: {},
            children: [
              new docx.Paragraph({
                children: [
                  new docx.ImageRun({
                    data: imageUrl,
                    transformation: {
                      width: 600,
                      height: 400,
                    },
                  }),
                ],
              }),
            ],
          }],
        });

        setProgress(50);
        const blob = await docx.Packer.toBlob(doc);
        setProgress(80);
        saveAs(blob, `${fileName.replace(/\.[^/.]+$/, "")}.docx`);
      }

      setProgress(100);
    } catch (error) {
      console.error("Conversion error:", error);
      alert(`Failed to create Word document: ${error.message}`);
    } finally {
      setIsConverting(false);
      setTimeout(() => setProgress(0), 500);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files[0]);
    e.target.value = null;
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    setImageUrl(null);
    setFileName("");
    setProgress(0);
    setExtractedText("");
    setOcrStatus("");
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <HeaderSection toolId="jpegToWordConverter" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white dark:text-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FontAwesomeIcon icon={faImage} className="mr-3" />
              Image to Word Converter
            </h1>
            <p className="opacity-90">
              Convert images to Word documents with optional text extraction
            </p>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Mode Selection */}
            <div className="mb-6 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
              <h3 className="font-medium mb-3">Conversion Mode:</h3>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="conversionMode"
                    checked={mode === "embed"}
                    onChange={() => setMode("embed")}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span>Embed Image (default)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="conversionMode"
                    checked={mode === "ocr"}
                    onChange={() => setMode("ocr")}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span>Extract Text (OCR)</span>
                </label>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                {mode === "embed" 
                  ? "Embeds the image directly in the Word document" 
                  : "Uses OCR to extract text from the image and creates an editable document"}
              </p>

              {mode === "ocr" && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-gray-800 rounded border border-blue-200 dark:border-gray-700">
                  <h4 className="font-medium mb-2">OCR Options:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={ocrOptions.preserveInterwordSpaces}
                          onChange={(e) => setOcrOptions({
                            ...ocrOptions,
                            preserveInterwordSpaces: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600"
                        />
                        Preserve interword spaces
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm">
                        <span>Page segmentation:</span>
                        <select
                          value={ocrOptions.tessedit_pageseg_mode}
                          onChange={(e) => setOcrOptions({
                            ...ocrOptions,
                            tessedit_pageseg_mode: e.target.value
                          })}
                          className="border rounded px-2 py-1 text-sm"
                        >
                          <option value="6">Single block (default)</option>
                          <option value="3">Fully automatic</option>
                          <option value="4">Single column</option>
                          <option value="11">Sparse text</option>
                        </select>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Section */}
            <div 
              ref={dropZoneRef}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer mb-6
                ${isDragging ? "border-green-500 bg-green-50" : "border-gray-300 hover:border-green-400"}`}
            >
              <div className="flex flex-col items-center justify-center gap-4">
                <FiUpload className="text-4xl text-blue-500" />
                <div>
                  <p className="font-medium text-lg">
                    {isDragging ? "Drop your image here" : "Drag & drop your JPEG/PNG file here"}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 mt-1">or</p>
                </div>
                <button
                  type="button"
                  className="px-6 py-2 bg-[#74D19C] text-white rounded-full hover:bg-blue-700 transition flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FiImage /> Select Image
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Supports JPEG, JPG, and PNG files
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg, image/png, image/jpg"
                onChange={handleInputChange}
                className="hidden"
                disabled={isConverting}
              />
            </div>

            {/* Progress Bar */}
            {isConverting && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                  <span>{ocrStatus || "Processing..."}</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* File Info */}
            {fileName && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6 flex items-center gap-3">
                <FiImage className="text-blue-600 text-xl" />
                <div>
                  <p className="font-medium">{fileName}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {isConverting ? "Processing..." : "Ready for conversion"}
                  </p>
                </div>
              </div>
            )}

            {/* Image Preview */}
            {imageUrl && !isConverting && (
              <div className="mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <FiImage /> Image Preview
                  </h2>
                  <div className="flex gap-2">
                    {mode === "ocr" && !extractedText && (
                      <button
                        onClick={performOCR}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                      >
                        <FiFileText /> Extract Text
                      </button>
                    )}
                    <button
                      onClick={handleConvertToWord}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                    >
                      <FaRegFileWord /> Convert to Word
                    </button>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-1 bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                    <img
                      src={imageUrl}
                      alt="Uploaded preview"
                      className="max-w-full h-auto max-h-[400px] rounded-md shadow-sm dark:shadow-gray-900/10 mx-auto"
                    />
                  </div>
                  {mode === "ocr" && extractedText && (
                    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                      <h3 className="font-medium mb-2">Extracted Text:</h3>
                      <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700 h-64 overflow-auto">
                        {extractedText || "No text could be extracted from this image."}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Instructions */}
            {!imageUrl && !isConverting && (
              <div className="mt-8 bg-gray-50 dark:bg-gray-900 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-lg mb-3 text-center">How to convert images to Word:</h3>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-white">
                  <li>Select conversion mode (embed image or extract text)</li>
                  <li>Upload your image file (JPEG/PNG)</li>
                  <li>Preview your image (and extracted text if using OCR)</li>
                  <li>Click "Convert to Word" button</li>
                  <li>Download your Word document</li>
                </ol>
                
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                  <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Tips for better OCR results:</h4>
                  <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                    <li>• Use high-resolution images (300dpi or higher)</li>
                    <li>• Ensure text is clear and not rotated</li>
                    <li>• Good lighting and contrast improves accuracy</li>
                    <li>• For documents, use "Single block" segmentation mode</li>
                    <li>• Enable "Preserve interword spaces" for better formatting</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-100 dark:bg-gray-800 p-4 text-center text-sm text-gray-600 dark:text-gray-300">
            <p>Convert images to Word documents with optional OCR text extraction</p>
          </div>
        </div>
      </div>
      <BottomSection toolId="jpegToWordConverter" />
    </>
  );
}